<template>
  <div class="home">
    <div v-if="shareDetails && !showError">
      <div class="row mt-3">
        <div class="col col-sm-9 justify-content-center align-self-end">
          <h1 v-if="shareDetails.evidenceItem.title" class="mx-0 mt-2 mb-1">{{shareDetails.evidenceItem.title}}</h1>
          <span class="mr-3"><strong>{{$t('evidence.media-type')}}:</strong> {{shareDetails.evidenceItem.mediaType}}</span>
          <span v-if="fileSize !== ''" class="mr-3"><strong>{{$t('evidence.file-size')}}:</strong> {{fileSize}}</span>
        </div>
        <div class="col-12 col-sm-3">
          <b-dropdown variant="secondary" right :popper-opts="popperOpts" class="mt-2 mr-0 float-right" v-if="shareDetails.evidenceItem.evidenceFiles.length > 0">
            <template slot="button-content">
                {{$t('evidence.downloads-button', { 'count': shareDetails.evidenceItem.evidenceFiles.length})}}
            </template>

            <b-dropdown-item 
              v-for="file in shareDetails.evidenceItem.evidenceFiles" 
              :key="file.fileId" 
              @click="downloadWithVirusCheck(file)">
              {{$t('evidence.variant-types.'+file.variantName)}}
              ({{ file.fileExtension }}) <span v-if="showFileSize(file)">({{file.fileSize}})</span>
            </b-dropdown-item>

          </b-dropdown>
        </div>
      </div>

      <div class="row mt-5 justify-content-center align-items-center vh-50" v-if="showVirusWarning">
        <div class="col-12 text-center">
          <div class="d-flex flex-column align-items-center">
            <span
              class="mb-3 virus-status-icon"
              aria-hidden="true"
            >
              <font-awesome-icon icon="exclamation-circle" class="fa-2x" />
            </span>
            <span class="lead text-left" data-testid="virus-status-message" 
              v-html="getVirusStatusMessage(virusCheckStatus)">
            </span>
          </div>
        </div>
      </div>
      
      <EvidenceMedia 
        v-if="!showVirusWarning"
        :data="shareDetails.evidenceItem" 
        :includeCommentsAndBookmarks="shareDetails.reportOptions.includeCommentsAndBookmarks" 
        v-on:getFileError="handleGetFileError">
      </EvidenceMedia>  
      <EvidenceDetails 
        :data="shareDetails" 
        :includeCommentsAndBookmarks="shareDetails.reportOptions.includeCommentsAndBookmarks">
      </EvidenceDetails>

    </div>
    <div class="row mt-5 justify-content-center align-items-center" v-else-if="showError && unshared">
      <h2 class="ml-2">{{$t('evidence.loading-unshared')}}</h2>
      <p class="lead text-muted">{{$t('evidence.loading-unshared-details')}}</p>
    </div>
    <div class="row mt-5 justify-content-center align-items-center" v-else-if="showError && extracting">
      <h2 class="ml-2">{{$t('evidence.loading-extracting')}}</h2>
      <p class="lead text-muted">{{$t('evidence.loading-extracting-details')}}</p>
    </div>

    <div class="row mt-5 justify-content-center align-items-center" v-else-if="showError">
      <h2 class="ml-2">{{$t('evidence.loading-error')}}</h2>
      <p class="lead text-muted">{{$t('evidence.loading-error-details')}}</p>
    </div>
    <div class="row mt-5 justify-content-center align-items-center" v-else>
      <h1><font-awesome-icon icon="spinner" pulse /></h1><h2 class="ml-2">{{$t('evidence.loading')}}</h2>
    </div>

    <download-virus-warning-window
      :visible="showVirusWarningModal"
      :virus-check-status="virusCheckStatus"
      @update:visible="showVirusWarningModal = $event"
      @confirm="proceedWithDownload" 
      @cancel="cancelDownload">
    </download-virus-warning-window>

    <div v-if="showError" class="alert alert-danger mt-3">
      <p class="lead font-weight-bold">{{$t('evidence.error-message')}}</p>
      <p>{{$t('evidence.error-details')}}</p>
    </div>
  </div>
</template>

<script lang="ts">
  import { Component } from 'vue-property-decorator';
  import apiClient from '@/services/apiClient';
  import { ShareDetails } from '@/types/data/shareDetails';
  import EvidenceDetails from '@/components/evidence/EvidenceDetails.vue';
  import EvidenceMedia from '@/components/evidence/EvidenceMedia.vue';
  import { buildFileUrl } from '@/services/apiClient';
  import languageService from '@/services/languageService';
  import BaseView from '@/components/BaseView';
  import { EvidenceFile } from '@/types/data/evidenceFile';
  import { VirusCheckStatus } from '@/types/enums/virusCheckStatus';
  import DownloadVirusWarningWindow from '@/components/DownloadVirusWarningWindow.vue';
import { TranslateResult } from 'vue-i18n';

  @Component({
    components: {
      EvidenceDetails,
      EvidenceMedia,
      DownloadVirusWarningWindow
    }
  })
  export default class EvidenceView extends BaseView {

    shareDetails: ShareDetails | null = null;
    showError: boolean = false;
    unshared: boolean = false;
    virusCheckStatus: string | undefined = undefined;
    extracting: boolean = false;
    fileSize: string = '';
    showVirusWarning: boolean = false;
    showVirusWarningModal: boolean = false;
    selectedFile: EvidenceFile | null = null;
    popperOpts: any = {
      modifiers: {
        computeStyle: {
          gpuAcceleration: false
        }
      }
    };

    fileUrl(file: EvidenceFile) {
      return buildFileUrl(this.$route.params.id, file.fileId, true);
    }

    handleGetFileError() {
      this.showError = true;
    }

    created() {
      if (this.$route.query.error) {
        this.setErrorFlags(this.$route.query.error);
        return;
      }

      this.retrieveAndPopulate(this.$route.params.id).then(x => {
        this.shareDetails = x;
        this.virusCheckStatus = this.shareDetails?.evidenceItem?.virusCheckStatus;

        this.showVirusWarning = this.shouldShowDownloadWarning();

        if (this.shareDetails) {
          this.fileSize = this.getFileSize(this.shareDetails);

          if (this.shareDetails.evidenceItem.title) {
            document.title = languageService.t('page-title.evidence', { title: this.shareDetails.evidenceItem.title });
          }
        }
      });
    }

    async retrieveAndPopulate(id: string): Promise<ShareDetails> {
      this.shareDetails = null;
      this.showError = false;
      this.virusCheckStatus = undefined;
      return apiClient.get(`share/${id}`)
        .then(response => {
          return response.data;
        })
        .catch(error => {
          console.error('Unable to retrieve evidence data', error);
          this.setErrorFlags(error);
        });
    }

    setErrorFlags(error: any): void {
      this.showError = true;
      if (!error.response) {
        return;
      }
      const errorCode = Number(error.response.status);
      if (isNaN(errorCode)) {
        return;
      }

      this.unshared =
        errorCode === 451 ||  // Unavailable For Legal Reasons
        errorCode === 410;    // Gone (deleted)
      this.extracting =
        errorCode === 406;    // Not Acceptable
    }

    getFileSize(shareDetails: ShareDetails): string {
      let fileSize = '';

      if (shareDetails.evidenceItem.evidenceFiles &&
        shareDetails.evidenceItem.evidenceFiles.length > 0) {
          const primaryMediaFile = shareDetails.evidenceItem.evidenceFiles[0];
          if (primaryMediaFile && this.showFileSize(primaryMediaFile)) {
            fileSize = primaryMediaFile.fileSize;
          }
      }

      return fileSize;
    }

    showFileSize(file: EvidenceFile): boolean {
      return file.fileSize.split(' ')[0] !== '0';
    }

    getVirusStatusMessage(virusCheckStatus: string): TranslateResult {
      if (!virusCheckStatus) {
        return this.$t('virus-check-messages.default');
      }
      
      switch (virusCheckStatus.toLowerCase()) {
        case VirusCheckStatus.ThreatDetected.toLowerCase():
          return this.$t('virus-check-messages.threat-detected');
        case VirusCheckStatus.CheckFailed.toLowerCase():
          return this.$t('virus-check-messages.check-failed');
        case VirusCheckStatus.FileTooLarge.toLowerCase():
          return this.$t('virus-check-messages.file-too-large');
        case VirusCheckStatus.TimeOut.toLowerCase():
          return this.$t('virus-check-messages.timeout');
        case VirusCheckStatus.Skipped.toLowerCase():
          return this.$t('virus-check-messages.skipped');
        case VirusCheckStatus.VirusCheckUnavailable.toLowerCase():
          return this.$t('virus-check-messages.virus-check-unavailable');
        default:
          return this.$t('virus-check-messages.default');
      }
    }

    downloadWithVirusCheck(file: EvidenceFile): void {
      this.showVirusWarningModal = this.showVirusWarning;
      this.selectedFile = file;
      if (this.showVirusWarningModal) {
        this.showDownloadWarningForFile();
      } else {
        this.proceedWithDownload();
      }
    }

    showDownloadWarningForFile(): void {
      this.showVirusWarning = true;
    }

    proceedWithDownload(): void {
      if (this.selectedFile) {
        window.location.href = this.fileUrl(this.selectedFile);
      }
    }

    cancelDownload(): void {
      this.showVirusWarningModal = false;
    }

    shouldShowDownloadWarning(): boolean {
      return this.virusCheckStatus !== null &&
              this.virusCheckStatus !== undefined &&
              this.virusCheckStatus !== '' &&
              String(this.virusCheckStatus).toLowerCase() !== String(VirusCheckStatus.NoThreat).toLowerCase();
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
  .vh-50 {
    height: 50vh;
  }

  .virus-status-icon {
    font-size: 2rem;
  }

</style>
